// Small pure helpers for bulk entry: header matching, dates, lookups, lists.
// No SheetJS here, so pages can import these without pulling the xlsx bundle
// into the main chunk (importers.js is the only module that loads SheetJS).

export const pad = (n) => String(n).padStart(2, '0');
export const cleanCell = (v) => (v == null ? '' : String(v).replace(/ /g, ' ').trim());
export const dropEmptyRows = (rows) => rows.filter((r) => r.some((c) => cleanCell(c) !== ''));

// ---- header guessing -----------------------------------------------------------

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const tokens = (s) => new Set(norm(s).split(' ').filter(Boolean));

/** How well a source header matches a grid column: exact label/key/alias = 1,
 *  otherwise token overlap, 0 if nothing in common. */
function headerScore(header, column) {
  const h = norm(header);
  if (!h) return 0;
  const candidates = [column.label, column.key, ...(column.aliases || [])].map(norm);
  if (candidates.includes(h)) return 1;
  const ht = tokens(header);
  let best = 0;
  candidates.forEach((c) => {
    const ct = tokens(c);
    if (!ct.size) return;
    let hit = 0;
    ct.forEach((tk) => { if (ht.has(tk)) hit += 1; });
    const score = hit / Math.max(ct.size, ht.size);
    if (score > best) best = score;
  });
  return best;
}

/** For each source column index, the grid column key it most likely is (or null).
 *  Each grid column is used at most once; best matches are assigned first. */
export function guessMapping(headers, columns) {
  const pairs = [];
  headers.forEach((h, i) => columns.forEach((c) => {
    if (GENERIC_HEADERS.has(norm(h))) return; // "Sl No", "Remarks": recognised, but map to nothing
    const s = headerScore(h, c);
    if (s >= 0.5) pairs.push({ i, key: c.key, s });
  }));
  pairs.sort((a, b) => b.s - a.s);
  const mapping = headers.map(() => null);
  const usedKeys = new Set();
  pairs.forEach((p) => {
    if (mapping[p.i] || usedKeys.has(p.key)) return;
    mapping[p.i] = p.key;
    usedKeys.add(p.key);
  });
  return mapping;
}

// Header words that appear in people's sheets but mean nothing to us (serial
// numbers, remarks). They count as headers so the row is recognised, and map to nothing.
const GENERIC_HEADERS = new Set(['s no', 'sno', 'sl no', 'slno', 'sr no', 'srno', 'serial', 'serial no', 'no', '#', 'remarks', 'remark', 'comments', 'comment', 'notes', 'note', 'status remarks']);

/** True when `value` is exactly a column's label, key or alias (ignoring case and punctuation), or a generic header word. */
export function isExactHeader(value, columns) {
  const v = norm(value);
  return !!v && (GENERIC_HEADERS.has(v) || columns.some((c) => [c.label, c.key, ...(c.aliases || [])].some((x) => norm(x) === v)));
}

/** Index of the first row that looks like a header (sheets often start with a
 *  title or a blank line), searching the first ten rows. -1 if none. */
export function findHeaderRow(rows, columns) {
  for (let i = 0; i < Math.min(10, rows.length); i++) {
    if (looksLikeHeader(rows[i], columns)) return i;
  }
  return -1;
}

/** "K Gagan <gagan@x.com>", "mailto:gagan@x.com", "gagan@x.com (Gagan)" -> gagan@x.com (lower-cased). Other text unchanged. */
export function extractEmail(value) {
  const s = String(value || '').trim();
  const m = s.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
  return m ? m[0].toLowerCase() : s;
}

/** "4h", "4.5 hrs", "1,234", "12 %" -> number; null if there is no number in it. */
export function parseNumber(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const m = String(value).replace(/,/g, '').match(/-?\d+(\.\d+)?/);
  return m ? Number(m[0]) : null;
}

/**
 * Make a pasted / imported value match what a column expects, where that can be
 * done safely. Returns the value unchanged when unsure (validation then flags it).
 *   select: option value, label or synonym (case-insensitive) -> option value
 *   email / email-like lookups: pull the address out of "Name <addr>" and lower-case it
 *   number: "4 hrs" -> "4"
 */
export function coerceValue(column, value) {
  const s = cleanCell(value);
  if (!s) return '';
  const lc = s.toLowerCase();
  if (column.type === 'select' && column.options) {
    const opt = column.options.find((o) => String(o.value).toLowerCase() === lc || String(o.label).toLowerCase() === lc);
    if (opt) return opt.value;
    const syn = column.synonyms && Object.entries(column.synonyms).find(([k]) => k.toLowerCase() === lc);
    if (syn) return syn[1];
    return s;
  }
  if (column.type === 'email') return extractEmail(s);
  const emailOptions = column.type === 'lookup' || column.type === 'multilookup' ? (Array.isArray(column.lookupOptions) && column.lookupOptions.length && column.lookupOptions.every((o) => /@/.test(o))) || column.emails : false;
  if (column.type === 'lookup' && emailOptions) return extractEmail(s);
  if (column.type === 'multilookup' && emailOptions) return splitList(s).map(extractEmail).join('; ');
  if (column.type === 'number') { const n = parseNumber(s); return n == null ? s : String(n); }
  return s;
}

/** Does the first row look like a header row rather than data? */
export function looksLikeHeader(firstRow, columns) {
  if (!firstRow || !firstRow.length) return false;
  const cells = firstRow.map(cleanCell).filter(Boolean);
  if (!cells.length) return false;
  // A row holding an email address, a number or a date is data, whatever else is in it.
  if (cells.some((v) => isEmailLike(v) || isNumberLike(v) || normalizeDate(v))) return false;
  const exact = cells.filter((c) => isExactHeader(c, columns) && !GENERIC_HEADERS.has(norm(c))).length;
  const generic = cells.filter((c) => GENERIC_HEADERS.has(norm(c))).length;
  const fuzzy = guessMapping(firstRow, columns).filter(Boolean).length;
  // Mostly exact column names, or a couple of exact ones backed by near matches.
  if (exact + generic >= 2 && exact >= 1) return true;
  if (exact >= 1 && fuzzy >= 2) return true;
  // One- or two-cell rows only count when a cell is exactly a known header,
  // so a title like "Project list" above the table is not taken for one.
  return cells.length <= 2 && exact >= 1;
}

// ---- headerless blocks: work out columns from their contents ------------------

const isEmailLike = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || /<[^<>\s]+@[^<>\s]+>/.test(v);
const isNumberLike = (v) => /^-?\d+([.,]\d+)?\s*[a-z%]{0,5}$/i.test(v); // 4, 2.5, 1,250, "4 hrs", "12%"
const isSerialRun = (vals) => vals.every((v, i) => Number(v) === i + 1);
const optionMatch = (column, v) => {
  const lc = v.toLowerCase();
  return (column.options || []).some((o) => String(o.value).toLowerCase() === lc || String(o.label).toLowerCase() === lc)
    || Object.keys(column.synonyms || {}).some((k) => k.toLowerCase() === lc);
};
const lookupMatch = (column, v) => Array.isArray(column.lookupOptions) && column.lookupOptions.some((o) => norm(o) === norm(v));
const idLike = (column) => /\b(id|code|number|no)\b/i.test(column.label) || /id$/i.test(column.key);

/**
 * For a block pasted WITHOUT a header row, guess which grid column each source
 * column is from what is in it: all emails -> the email column, all numbers ->
 * an ID / number column, all role names -> the role dropdown, all dates -> a
 * date column, names that exist in a picker -> that picker, and whatever text
 * is left -> the remaining text columns in order. A 1, 2, 3… column is treated
 * as a serial number and skipped.
 * Returns { mapping, confident }: confident counts columns decided by content.
 */
export function inferMapping(rows, columns, dayFirst = true) {
  const width = Math.max(0, ...rows.map((r) => r.length));
  const mapping = Array(width).fill(null);
  const used = new Set();
  const take = (i, c) => { if (c && !used.has(c.key)) { mapping[i] = c.key; used.add(c.key); return true; } return false; };
  // Candidates in definition order, except a column with a higher `inferPriority`
  // wins a tie (e.g. a lone email column on Tasks is the assignee, not the owner).
  const byPriority = [...columns].sort((a, b) => (b.inferPriority || 0) - (a.inferPriority || 0));
  const free = (pred) => byPriority.find((c) => !used.has(c.key) && pred(c));
  const valuesOf = (i) => rows.map((r) => cleanCell(r[i])).filter(Boolean);
  const decided = new Set();
  const colVals = Array.from({ length: width }, (_, i) => valuesOf(i));

  // Pass 1: strong signals. Emails, dropdown values, dates, numbers.
  colVals.forEach((vals, i) => {
    if (!vals.length) return;
    if (vals.every(isEmailLike)) {
      if (take(i, free((c) => c.type === 'email')) || take(i, free((c) => (c.type === 'lookup' || c.type === 'multilookup') && c.emails))) decided.add(i);
      return;
    }
    // Most values matching is enough: a list of roles may contain one title we don't have ("Assistant Manager").
    const sel = free((c) => c.type === 'select' && vals.filter((v) => optionMatch(c, v)).length >= Math.ceil(vals.length * 0.6));
    if (sel && take(i, sel)) { decided.add(i); return; }
    if (vals.every(isNumberLike)) {
      if (isSerialRun(vals) && vals.length > 1) { decided.add(i); return; } // serial number column: skip
      // Long whole numbers (employee IDs, potential IDs) are identifiers; short or
      // decimal ones (4, 2.5, 120) are quantities.
      const idish = vals.every((v) => /^\d{4,}$/.test(v)); // bare long integers only; "4 hrs" is a quantity
      const first = idish ? free((c) => c.type === 'text' && idLike(c)) : free((c) => c.type === 'number');
      const second = idish ? free((c) => c.type === 'number') : free((c) => c.type === 'text' && idLike(c));
      if (take(i, first) || take(i, second)) decided.add(i);
      return;
    }
    if (vals.every((v) => normalizeDate(v, dayFirst)) && take(i, free((c) => c.type === 'date'))) decided.add(i);
  });
  // Pass 2: values that exist in a picker's list (project names, services).
  colVals.forEach((vals, i) => {
    if (decided.has(i) || !vals.length) return;
    const hits = (c) => vals.filter((v) => lookupMatch(c, v)).length;
    const best = columns.filter((c) => !used.has(c.key) && c.type === 'lookup' && !c.emails).map((c) => ({ c, n: hits(c) })).sort((a, b) => b.n - a.n)[0];
    if (best && best.n >= Math.ceil(vals.length / 2) && take(i, best.c)) decided.add(i);
  });
  // Pass 3: whatever text is left goes to the remaining text-ish columns, required
  // ones first. Email columns are never filled with plain text.
  const rest = columns.filter((c) => !used.has(c.key) && ['text', 'lookup', 'multilookup'].includes(c.type) && !c.emails);
  const order = [...rest.filter((c) => c.required), ...rest.filter((c) => !c.required)];
  colVals.forEach((vals, i) => {
    if (mapping[i] || decided.has(i) || !vals.length) return;
    const next = order.find((c) => !used.has(c.key));
    take(i, next);
  });
  return { mapping, confident: decided.size };
}

/** Apply a mapping (source index -> column key) to data rows -> grid value objects. */
export function applyMapping(rows, mapping) {
  return rows.map((r) => {
    const values = {};
    mapping.forEach((key, i) => { if (key) values[key] = cleanCell(r[i]); });
    return values;
  });
}

// ---- dates -------------------------------------------------------------------

const isoOf = (y, m, d) => {
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return `${y}-${pad(m)}-${pad(d)}`;
};
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/**
 * Turn whatever someone typed or pasted into YYYY-MM-DD, or null if it isn't a date.
 * `dayFirst` settles the ambiguous "03/04/2026" case (true = 3 April, false = 4 March).
 * Handles ISO, d/m/y and m/d/y with - / or ., "15 May 2026", "May 15, 2026",
 * and Excel serial numbers (what a date cell becomes when a file is read raw).
 */
export function normalizeDate(value, dayFirst = true) {
  if (value == null || value === '') return null;
  if (value instanceof Date && !isNaN(value)) return isoOf(value.getFullYear(), value.getMonth() + 1, value.getDate());
  const s = String(value).trim().replace(/(\d)(st|nd|rd|th)\b/gi, '$1'); // 15th May -> 15 May
  let m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:[T ].*)?$/);
  if (m) return isoOf(+m[1], +m[2], +m[3]);
  m = s.match(/^(\d{1,2})[/.\s-]+([a-z]{3,})[/.\s-]+(\d{2,4})$/i); // 15-May-26, 15 May 2026
  if (m) {
    const mi = MONTHS.indexOf(m[2].slice(0, 3).toLowerCase());
    let y = +m[3]; if (y < 100) y += 2000;
    if (mi >= 0) return isoOf(y, mi + 1, +m[1]);
  }
  m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/);
  if (m) {
    let [, a, b, y] = m;
    y = +y; if (y < 100) y += 2000;
    a = +a; b = +b;
    let day; let month;
    if (a > 12 && b <= 12) { day = a; month = b; }
    else if (b > 12 && a <= 12) { day = b; month = a; }
    else if (dayFirst) { day = a; month = b; }
    else { day = b; month = a; }
    return isoOf(y, month, day);
  }
  m = s.match(/^([a-z]{3,})\.?\s+(\d{1,2}),?\s+(\d{4})$/i); // May 15, 2026
  if (m) {
    const mi = MONTHS.indexOf(m[1].slice(0, 3).toLowerCase());
    if (mi >= 0) return isoOf(+m[3], mi + 1, +m[2]);
  }
  if (/^\d{5}(\.\d+)?$/.test(s)) { // Excel serial (days since 1899-12-30)
    const serial = Math.floor(+s);
    if (serial > 20000 && serial < 80000) {
      const dt = new Date(Date.UTC(1899, 11, 30) + serial * 86400000);
      return isoOf(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
    }
  }
  return null;
}

/** "Thu 15 May 2026" for a YYYY-MM-DD string, so the person can confirm the reading. */
export const prettyDate = (iso) => (iso
  ? new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
  : '');

// ---- lookups ---------------------------------------------------------------------

/**
 * Closest option label to `value` (case/space-insensitive, then prefix, then
 * token overlap), or null. Tokens shared by most labels (an email domain, a
 * common prefix like "EDGE") carry no weight. An email address never snaps
 * to a different address: only an exact match counts.
 */
export function closestLabel(value, labels) {
  const v = norm(value);
  if (!v || !labels?.length) return null;
  const exact = labels.find((l) => norm(l) === v);
  if (exact) return exact;
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim())) return null;
  const df = new Map();
  labels.forEach((l) => tokens(l).forEach((t) => df.set(t, (df.get(t) || 0) + 1)));
  const common = new Set([...df].filter(([, n]) => n > Math.max(2, labels.length * 0.4)).map(([t]) => t));
  const meaningful = (s) => new Set([...tokens(s)].filter((t) => !common.has(t)));
  const vt = meaningful(value);
  if (!vt.size) return null;
  const prefix = labels.find((l) => { const n = norm(l); return n.startsWith(v) || (v.length >= 4 && v.startsWith(n)); });
  if (prefix) return prefix;
  let best = null; let bestScore = 0;
  labels.forEach((l) => {
    const lt = meaningful(l);
    if (!lt.size) return;
    let hit = 0;
    lt.forEach((t) => { if (vt.has(t)) hit += 1; });
    const score = hit / Math.max(lt.size, vt.size);
    if (score > bestScore) { bestScore = score; best = l; }
  });
  return bestScore > 0.5 ? best : null;
}

export const splitList = (s) => String(s || '').split(/[;,\n]/).map((x) => x.trim()).filter(Boolean);
export const joinList = (arr) => (arr || []).filter(Boolean).join('; ');
